'use client'

import { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { Mail, Pencil, RefreshCw, X } from 'lucide-react'
import { getSession, signIn, useSession } from 'next-auth/react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { withResource } from '@/shared/contexts/Resource'
import { FormItem } from '@/shared/form/FormItem'
import { Input } from '@/shared/form/Input'
import { PasswordInput } from '@/shared/form/PasswordInput'
import { ResourceForm } from '@/shared/form/ResourceForm'
import { cn } from '@/shared/lib/utils'
import { User } from '@/shared/types/user'
import { Button } from '@/shared/ui/Button'
import { inputClassName } from '@/shared/ui/input-styles'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle
} from '@/shared/ui/Modal'

import { useCurrentUserRecord } from '../../../hooks/useCurrentUserRecord'
import { useSecuritySettings } from '../../../hooks/useSecuritySettings'

import { nameSchema } from './validationSchema'

const profileSchema = z.object({
  name: nameSchema
})

const emailSchema = z.object({
  email: z.string().min(1, 'E-mail é obrigatório.').email('E-mail em formato inválido.'),
  current_password: z.string().min(1, 'Senha atual é obrigatória.')
})

const passwordSchema = z
  .object({
    current_password: z.string().min(1, 'Senha atual é obrigatória.'),
    password: z.string().min(6, 'A nova senha deve ter ao menos 6 caracteres.'),
    password_confirmation: z.string().min(1, 'Confirme a nova senha.')
  })
  .refine(d => d.password === d.password_confirmation, {
    message: 'As senhas não coincidem.',
    path: ['password_confirmation']
  })

type EmailFormData = z.infer<typeof emailSchema>
type PasswordFormData = z.infer<typeof passwordSchema>
type TabId = 'profile' | 'email' | 'password'

function CredentialRow({
  label,
  value,
  editing,
  onEdit,
  children
}: {
  label: string
  value: string
  editing: boolean
  onEdit: () => void
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {!editing && (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            <Pencil className="size-3" />
            Editar
          </Button>
        )}
      </div>
      {!editing && (
        <div className="rounded-lg bg-muted/40 px-3 py-2.5 text-sm font-medium text-foreground">
          {value}
        </div>
      )}
      {editing && children}
    </div>
  )
}

function EmailTab() {
  const [editing, setEditing] = useState(false)
  const { data: session } = useSession()
  const { updateCredentials, resendEmailConfirmation, cancelEmailChange, isLoading } =
    useSecuritySettings()
  const { currentUser, refetch } = useCurrentUserRecord()

  const unconfirmedEmail = currentUser?.unconfirmed_email ?? null
  const isPending = !!unconfirmedEmail

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: '', current_password: '' }
  })

  const cancelEdit = () => {
    setEditing(false)
    form.reset()
  }

  const onSubmit = async (data: EmailFormData) => {
    try {
      await updateCredentials(data)
      const sess = await getSession()
      if (sess) {
        await signIn('refresh-session', {
          token: (sess as any).accessToken,
          redirect: false
        })
      }
      toast.success('Confirmação enviada. Verifique seu e-mail.')
      cancelEdit()
      refetch()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const errors = error.response.data?.errors as Record<string, string[]>
        if (errors) {
          Object.entries(errors).forEach(([key, msgs]) => {
            form.setError(key as keyof EmailFormData, { message: msgs.join(', ') })
          })
        }
      }
    }
  }

  const handleResend = async () => {
    try {
      await resendEmailConfirmation()
      toast.success('E-mail de confirmação reenviado.')
    } catch {
      toast.error('Não foi possível reenviar. Tente novamente.')
    }
  }

  const handleCancelChange = async () => {
    try {
      await cancelEmailChange()
      toast.success('Troca de e-mail cancelada.')
      refetch()
    } catch {
      toast.error('Não foi possível cancelar. Tente novamente.')
    }
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Confirmação pendente
          </span>
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
            <Mail className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-amber-900">
                Verifique seu e-mail
              </span>
              <span className="text-xs text-amber-700">
                Enviamos uma confirmação para{' '}
                <strong>{unconfirmedEmail}</strong>. Clique no link para confirmar a troca.
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={isLoading}
            onClick={handleResend}
          >
            <RefreshCw className="size-3" />
            Reenviar e-mail
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={isLoading}
            onClick={handleCancelChange}
          >
            <X className="size-3" />
            Cancelar troca
          </Button>
        </div>
        <div className="rounded-lg bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground">
          E-mail atual: <span className="font-medium text-foreground">{session?.user?.email ?? '—'}</span>
        </div>
      </div>
    )
  }

  return (
    <CredentialRow
      label="E-mail atual"
      value={session?.user?.email ?? '—'}
      editing={editing}
      onEdit={() => setEditing(true)}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <FormItem name="email">
            <FormItem.Label>Novo e-mail</FormItem.Label>
            <FormItem.Control>
              <Input className={inputClassName} placeholder="novo@email.com" />
            </FormItem.Control>
          </FormItem>
          <FormItem name="current_password">
            <FormItem.Label required>Senha atual</FormItem.Label>
            <FormItem.Control>
              <PasswordInput name="current_password" className={inputClassName} />
            </FormItem.Control>
          </FormItem>
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? 'Enviando...' : 'Confirmar troca'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={cancelEdit}>
              <X className="size-3" />
              Cancelar
            </Button>
          </div>
        </form>
      </FormProvider>
    </CredentialRow>
  )
}

function PasswordTab() {
  const [editing, setEditing] = useState(false)
  const { updateCredentials, isLoading } = useSecuritySettings()

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: '', password: '', password_confirmation: '' }
  })

  const cancel = () => {
    setEditing(false)
    form.reset()
  }

  const onSubmit = async (data: PasswordFormData) => {
    try {
      await updateCredentials(data)
      toast.success('Senha atualizada com sucesso.')
      cancel()
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const errors = error.response.data?.errors as Record<string, string[]>
        if (errors) {
          Object.entries(errors).forEach(([key, msgs]) => {
            form.setError(key as keyof PasswordFormData, { message: msgs.join(', ') })
          })
        }
      }
    }
  }

  return (
    <CredentialRow
      label="Senha"
      value="••••••••••••"
      editing={editing}
      onEdit={() => setEditing(true)}
    >
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3">
          <FormItem name="current_password">
            <FormItem.Label required>Senha atual</FormItem.Label>
            <FormItem.Control>
              <PasswordInput name="current_password" className={inputClassName} />
            </FormItem.Control>
          </FormItem>
          <FormItem name="password">
            <FormItem.Label required>Nova senha</FormItem.Label>
            <FormItem.Control>
              <PasswordInput name="password" className={inputClassName} />
            </FormItem.Control>
          </FormItem>
          <FormItem name="password_confirmation">
            <FormItem.Label required>Confirmar nova senha</FormItem.Label>
            <FormItem.Control>
              <PasswordInput name="password_confirmation" className={inputClassName} />
            </FormItem.Control>
          </FormItem>
          <div className="flex gap-2">
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={cancel}>
              <X className="size-3" />
              Cancelar
            </Button>
          </div>
        </form>
      </FormProvider>
    </CredentialRow>
  )
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'profile', label: 'Perfil' },
  { id: 'email', label: 'E-mail' },
  { id: 'password', label: 'Senha' }
]

export const UserProfileEditModal = withResource(
  ({ onSave }: { onSave?: () => void }) => {
    const [activeTab, setActiveTab] = useState<TabId>('profile')

    const handleProfileSave = async () => {
      const session = await getSession()
      if (session) {
        await signIn('refresh-session', {
          token: (session as any).accessToken,
          redirect: false
        })
      }
      onSave?.()
    }

    return (
      <Modal asCard>
        <ModalHeader>
          <ModalTitle>Editar Conta</ModalTitle>
        </ModalHeader>

        <div className="flex border-b px-6 gap-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'profile' && (
          <ResourceForm<User>
            onSave={handleProfileSave}
            validationSchema={profileSchema as any}
          >
            <ModalBody className="space-y-4">
              <FormItem name="name">
                <FormItem.Label>Nome</FormItem.Label>
                <FormItem.Control>
                  <Input className={inputClassName} />
                </FormItem.Control>
              </FormItem>
            </ModalBody>
            <ModalFooter>
              <Button type="submit" variant="primary" className="w-full">
                Salvar
              </Button>
            </ModalFooter>
          </ResourceForm>
        )}

        {activeTab === 'email' && (
          <ModalBody>
            <EmailTab />
          </ModalBody>
        )}

        {activeTab === 'password' && (
          <ModalBody>
            <PasswordTab />
          </ModalBody>
        )}
      </Modal>
    )
  },
  {
    apiBasePath: '/user',
    isEdit: true,
    forceIdPresence: false,
    shouldFetch: true
  }
)
